import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import axiosConn from "config/axiosConn";
import SpinnerOverlay from "../Components/SchoolAdmin/Spinner";
import Helper from "../helper/Common_Helper";
import ThemeBaseRouting from "../helper/ThemeBaseRouting";
import SubThemesList from "../helper/SubThemes_List";

const PAGE_SIZE_OPTIONS = [12, 24, 36, 54];

// district is stored as a bare numeric id on many events; hide those
const cleanPlace = (district, state) =>
  [/^\d+$/.test(String(district || "").trim()) ? "" : district, state]
    .filter(Boolean)
    .join(", ");

const subThemeLabel = (sub_themes) =>
  Array.isArray(sub_themes) ? sub_themes.filter(Boolean).join(", ") : sub_themes || "";

function ImageGallery() {
  const [images, setImages] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [loading, setLoading] = useState(false);
  const [failedUrls, setFailedUrls] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const [filters, setFilters] = useState({ theme_name: "", sub_theme: "" });

  const getGalleryImages = async (page = 1, limit = pageSize, applied = filters) => {
    try {
      setLoading(true);
      const response = await axiosConn("post", "get-gallery-images", {
        page,
        limit,
        theme_name: applied.theme_name,
        sub_theme: applied.sub_theme,
      });
      if (response.data.status === "success") {
        setImages(response.data.data || []);
        setTotalPages(response.data.totalPages || 1);
        setCurrentPage(response.data.currentPage || page);
        setFailedUrls([]);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getGalleryImages(1, pageSize);
  }, []);

  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages && page !== currentPage && !loading) {
      getGalleryImages(page, pageSize);
    }
  };

  const handlePageSizeChange = (size) => {
    const limit = parseInt(size) || 12;
    setPageSize(limit);
    getGalleryImages(1, limit);
  };

  // picking a theme clears any sub-theme (sub-theme list depends on the theme)
  const handleThemeChange = (theme_name) => {
    const applied = { theme_name, sub_theme: "" };
    setFilters(applied);
    getGalleryImages(1, pageSize, applied);
  };

  const handleSubThemeChange = (sub_theme) => {
    const applied = { ...filters, sub_theme };
    setFilters(applied);
    getGalleryImages(1, pageSize, applied);
  };

  const subThemeOptions = filters.theme_name
    ? (SubThemesList[filters.theme_name] || []).map((s) => s.value.trim())
    : [];

  // hide any image the browser fails to load so no blank tiles are shown
  const handleImageError = (url) => {
    setFailedUrls((prev) => (prev.includes(url) ? prev : [...prev, url]));
  };

  const visibleImages = images.filter(
    (img) => img.photo_url && !failedUrls.includes(img.photo_url)
  );

  const pageNumbers = () => {
    const windowSize = 5;
    let start = Math.max(1, currentPage - Math.floor(windowSize / 2));
    let end = Math.min(totalPages, start + windowSize - 1);
    start = Math.max(1, end - windowSize + 1);
    const pages = [];
    for (let p = start; p <= end; p += 1) pages.push(p);
    return pages;
  };

  return (
    <>
      {loading && <SpinnerOverlay />}
      <section className="sevenScopeSec eventGallerySec">
        <div className="container-fluid">
          <div className="eventGalleryHead">
            <h2 className="text-center">
              Event <span className="hdngBrkr">Image Gallery</span>
            </h2>
            <p className="text-center galleryIntro">
              Glimpses from events organised by Eco Clubs across the country
              under Ek Ped Maa Ke Naam 3.0
            </p>
          </div>

          <div className="eventGalleryToolbar">
            <div className="eventGalleryCount">
              {totalPages > 0 && (
                <>
                  Page {currentPage.toLocaleString("en-IN")} of{" "}
                  {totalPages.toLocaleString("en-IN")}
                </>
              )}
            </div>
            <div className="eventGalleryFilters">
              <select
                aria-label="Filter by theme"
                value={filters.theme_name}
                disabled={loading}
                onChange={(e) => handleThemeChange(e.target.value)}
              >
                <option value="">All Themes</option>
                {ThemeBaseRouting.map((theme) => (
                  <option key={theme.value} value={theme.value}>
                    {theme.lable}
                  </option>
                ))}
              </select>
              <select
                aria-label="Filter by sub-theme"
                value={filters.sub_theme}
                disabled={loading || !filters.theme_name}
                onChange={(e) => handleSubThemeChange(e.target.value)}
              >
                <option value="">
                  {filters.theme_name ? "All Sub Themes" : "Select theme first"}
                </option>
                {subThemeOptions.map((sub) => (
                  <option key={sub} value={sub}>
                    {sub}
                  </option>
                ))}
              </select>
            </div>
            <div className="eventGalleryPageSize">
              <label htmlFor="gallerySize">Images per page</label>
              <select
                id="gallerySize"
                value={pageSize}
                disabled={loading}
                onChange={(e) => handlePageSizeChange(e.target.value)}
              >
                {PAGE_SIZE_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="eventGalleryGrid">
            {visibleImages.length > 0 ? (
              visibleImages.map((img, i) => (
                <div
                  className="eventGalleryCard"
                  key={`${img.photo_url}-${i}`}
                  onClick={() => setLightbox(img)}
                >
                  <div className="eventGalleryImgWrap">
                    <img
                      src={img.photo_url}
                      alt={img.event_name || "Eco Club event"}
                      loading="lazy"
                      onError={() => handleImageError(img.photo_url)}
                    />
                    {img.theme_name && (
                      <span className="eventGalleryThemeBadge">
                        {img.theme_name}
                      </span>
                    )}
                  </div>
                  <div className="eventGalleryCaption">
                    <strong title={img.event_name}>{img.event_name}</strong>
                    {subThemeLabel(img.sub_themes) && (
                      <span
                        className="eventGallerySubTheme"
                        title={subThemeLabel(img.sub_themes)}
                      >
                        {subThemeLabel(img.sub_themes)}
                      </span>
                    )}
                    <span title={img.school_name}>{img.school_name}</span>
                    <span>{cleanPlace(img.district, img.state)}</span>
                    {img.createdAt && (
                      <span className="eventGalleryDate">
                        Uploaded: {Helper.formatDate(img.createdAt)}
                      </span>
                    )}
                  </div>
                </div>
              ))
            ) : (
              !loading && <p className="text-center w-100">No images found.</p>
            )}
          </div>

          {totalPages > 1 && (
            <div className="eventGalleryPagination">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || loading}
              >
                &laquo; Previous
              </button>
              {pageNumbers().map((p) => (
                <button
                  key={p}
                  className={p === currentPage ? "active" : ""}
                  onClick={() => handlePageChange(p)}
                  disabled={loading}
                >
                  {p.toLocaleString("en-IN")}
                </button>
              ))}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages || loading}
              >
                Next &raquo;
              </button>
            </div>
          )}
        </div>
      </section>

      <Modal
        show={!!lightbox}
        onHide={() => setLightbox(null)}
        size="lg"
        centered
      >
        <span onClick={() => setLightbox(null)} className="closebtn">
          X
        </span>
        <Modal.Body style={{ padding: 20 }}>
          {lightbox && (
            <>
              <img
                src={lightbox.photo_url}
                alt={lightbox.event_name || "Eco Club event"}
                style={{ width: "100%", borderRadius: 8 }}
                onError={() => {
                  handleImageError(lightbox.photo_url);
                  setLightbox(null);
                }}
              />
              <div className="eventGalleryLightboxMeta">
                <strong>{lightbox.event_name}</strong>
                <div>
                  {[lightbox.theme_name, subThemeLabel(lightbox.sub_themes)]
                    .filter(Boolean)
                    .join(" • ")}
                </div>
                <div>{lightbox.school_name}</div>
                <div>{cleanPlace(lightbox.district, lightbox.state)}</div>
                <div>
                  {lightbox.event_date
                    ? `Event date: ${Helper.formatDate(lightbox.event_date)}`
                    : ""}
                  {lightbox.createdAt
                    ? `${lightbox.event_date ? " | " : ""}Uploaded: ${Helper.formatDate(lightbox.createdAt)}`
                    : ""}
                </div>
              </div>
            </>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
}

export default ImageGallery;
